/**
 * JSON-LD Schema helpers for Technical SEO
 */

import { HOME_CANONICAL_URL, SITE_ORIGIN, siteConfig, toAbsoluteUrl } from '../config';

const siteName = siteConfig.siteName;

export interface Game {
  slug: string;
  title: string;
  description: string;
  thumbnail: string;
  playUrl: string;
  developer?: string;
  rating?: string;
  votes?: string;
  released?: string;
  publishedDate?: string;
  lastUpdated?: string;
  categories: string[];
  minPlayers?: number;
  maxPlayers?: number;
  technology?: string;
  platforms?: string;
  howToPlay?: string;
  gameTips?: Array<{ title: string; tip: string }>;
  faqs?: Array<{ question: string; answer: string }>;
}

/**
 * Map category slugs to human-readable genre names for schema.org
 */
const GENRE_MAP: Record<string, string> = {
  action: 'Action',
  adventure: 'Adventure',
  arcade: 'Arcade',
  fighting: 'Fighting',
  franchise: 'Horror',
  horror: 'Horror',
  multiplayer: 'Multiplayer',
  puzzle: 'Puzzle',
  racing: 'Racing',
  running: 'Running',
  shooting: 'Shooter',
  sports: 'Sports',
  strategy: 'Strategy',
};

function mapGenres(categories: string[]): string[] {
  return [...new Set(categories.map((slug) => GENRE_MAP[slug] ?? slug.charAt(0).toUpperCase() + slug.slice(1)))];
}

function assetUrl(pathOrUrl: string): string {
  if (pathOrUrl.startsWith('http')) return pathOrUrl;
  return `${SITE_ORIGIN}${pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`}`;
}

function buildAggregateRating(game: Game) {
  if (!game.rating || !game.votes) return undefined;
  const count = game.votes.replace(/,/g, '');
  return {
    '@type': 'AggregateRating',
    ratingValue: game.rating,
    ratingCount: count,
    reviewCount: count,
    bestRating: '5',
    worstRating: '1',
  };
}

function toVideoGameEntity(game: Game, options?: { url?: string }) {
  const aggregateRating = buildAggregateRating(game);
  return {
    '@type': 'VideoGame' as const,
    name: game.title,
    description: game.description,
    image: assetUrl(game.thumbnail),
    genre: mapGenres(game.categories),
    applicationCategory: 'Game',
    operatingSystem: 'Web Browser',
    url: toAbsoluteUrl(options?.url ?? `/${game.slug}`),
    ...(aggregateRating ? { aggregateRating } : {}),
  };
}

/**
 * Organization & WebSite Schema for Homepage
 */
export function buildHomePageSchema() {
  const organization = {
    '@type': 'Organization',
    name: siteName,
    url: HOME_CANONICAL_URL,
    logo: {
      '@type': 'ImageObject',
      url: assetUrl('/assets/img/logo.png'),
    },
  };

  const website = {
    '@type': 'WebSite',
    name: siteName,
    url: HOME_CANONICAL_URL,
    inLanguage: 'en',
    description: `Play ${siteName} free online — no download, no install. Works instantly in any browser on desktop or mobile.`,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${toAbsoluteUrl('/search')}?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };

  return {
    '@context': 'https://schema.org',
    '@graph': [organization, website],
  };
}

/**
 * WebSite Schema (SearchAction when the site has search).
 * Use on the homepage alongside existing game schemas — do not replace them.
 */
export function buildWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: siteName,
    url: HOME_CANONICAL_URL,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${toAbsoluteUrl('/search')}?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };
}

/**
 * Homepage BreadcrumbList — matches the visible trail (Home, optional category, optional current page).
 */
export function buildHomeBreadcrumbSchema(options?: {
  categoryName?: string;
  categorySlug?: string;
  currentName?: string;
  currentUrl?: string;
}) {
  const items: Array<{ '@type': 'ListItem'; position: number; name: string; item: string }> = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: HOME_CANONICAL_URL,
    },
  ];

  if (options?.categoryName && options?.categorySlug) {
    items.push({
      '@type': 'ListItem',
      position: items.length + 1,
      name: options.categoryName,
      item: toAbsoluteUrl(`/category/${options.categorySlug}`),
    });
  }

  if (options?.currentName) {
    items.push({
      '@type': 'ListItem',
      position: items.length + 1,
      name: options.currentName,
      item: toAbsoluteUrl(options.currentUrl ?? '/'),
    });
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
}

/**
 * ItemList of VideoGame entities for homepages that list multiple titles.
 */
export function buildVideoGameItemListSchema(games: Game[], name = 'Featured Games') {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name,
    numberOfItems: games.length,
    itemListElement: games.map((game, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: toVideoGameEntity(game),
    })),
  };
}

/**
 * VideoGame Schema for Game Pages / homepage featured game.
 */
export function buildGameSchema(game: Game, options?: { url?: string }) {
  const aggregateRating = buildAggregateRating(game);

  // Use lastUpdated if available, otherwise fall back to publishedDate (never use dynamic date)
  const modifiedDate = game.lastUpdated || game.publishedDate || game.released;

  const numberOfPlayers = game.minPlayers && game.maxPlayers ? {
    '@type': 'QuantitativeValue',
    minValue: game.minPlayers,
    maxValue: game.maxPlayers,
  } : undefined;

  // Determine playMode based on player count: multi-player games get MultiPlayer
  const playMode = game.maxPlayers && game.maxPlayers > 1
    ? ['MultiPlayer', 'SinglePlayer']
    : 'SinglePlayer';

  return {
    '@context': 'https://schema.org',
    '@type': 'VideoGame',
    name: game.title,
    description: game.description,
    url: toAbsoluteUrl(options?.url ?? `/${game.slug}`),
    image: assetUrl(game.thumbnail),
    genre: mapGenres(game.categories),
    inLanguage: 'en',
    isAccessibleForFree: true,
    author: game.developer ? {
      '@type': 'Organization',
      name: game.developer,
    } : undefined,
    publisher: {
      '@type': 'Organization',
      name: siteName,
      url: HOME_CANONICAL_URL,
    },
    datePublished: game.publishedDate || game.released,
    dateModified: modifiedDate,
    applicationCategory: 'Game',
    applicationSubCategory: mapGenres(game.categories)[0] ?? 'VideoGame',
    operatingSystem: 'Web Browser',
    softwareVersion: '1.0',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
    },
    aggregateRating,
    gamePlatform: ['Web Browser', 'Desktop', 'Mobile'],
    playMode,
    numberOfPlayers,
  };
}

/**
 * HowTo Schema derived from howToPlay text.
 * Splits on double-newline blocks to create distinct steps — each step = unique content signal.
 * Returns null if howToPlay is absent or too short to parse meaningfully.
 */
export function buildHowToPlaySchema(game: Game): Record<string, unknown> | null {
  if (!game.howToPlay || game.howToPlay.trim().length < 100) return null;

  // Split into logical blocks (sections separated by blank lines)
  const rawBlocks = game.howToPlay
    .split(/\n{2,}/)
    .map((b) => b.trim())
    .filter((b) => b.length > 20);

  if (rawBlocks.length < 2) return null;

  const steps = rawBlocks.map((block, i) => {
    // Use first line as step name, rest as directions text
    const lines = block.split('\n');
    const name = lines[0].replace(/^(Step \d+[:.]?\s*)/i, '').trim();
    const text = lines.slice(1).join(' ').trim() || name;
    return {
      '@type': 'HowToStep',
      position: i + 1,
      name: name.length > 80 ? name.slice(0, 80) + '…' : name,
      text,
      url: `${toAbsoluteUrl(`/${game.slug}`)}#how-to-play`,
    };
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: `How to Play ${game.title}`,
    description: `Step-by-step guide to playing ${game.title} online for free in your browser.`,
    image: assetUrl(game.thumbnail),
    totalTime: 'PT5M',
    tool: [
      { '@type': 'HowToTool', name: 'Web Browser' },
    ],
    step: steps,
  };
}

/**
 * BreadcrumbList Schema for Game Pages
 * Pass options.pageUrl on single-game homepage so the current-page crumb matches canonical.
 */
export function buildGameBreadcrumbSchema(
  game: Game,
  categoryName?: string,
  categorySlug?: string,
  options?: { pageUrl?: string },
) {
  const pageUrl = toAbsoluteUrl(options?.pageUrl ?? `/${game.slug}`);
  const items = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: HOME_CANONICAL_URL,
    },
  ];

  if (categoryName && categorySlug) {
    items.push({
      '@type': 'ListItem',
      position: 2,
      name: categoryName,
      item: toAbsoluteUrl(`/category/${categorySlug}`),
    });
    items.push({
      '@type': 'ListItem',
      position: 3,
      name: game.title,
      item: pageUrl,
    });
  } else {
    items.push({
      '@type': 'ListItem',
      position: 2,
      name: game.title,
      item: pageUrl,
    });
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
}

/**
 * CollectionPage Schema for Category Pages
 */
export function buildCategorySchema(categoryName: string, categorySlug: string, description: string, gamesCount: number) {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} Games`,
    description,
    url: toAbsoluteUrl(`/category/${categorySlug}`),
    mainEntity: {
      '@type': 'ItemList',
      name: `${categoryName} Games`,
      description,
      numberOfItems: gamesCount,
    },
  };
}

/**
 * BreadcrumbList Schema for Category Pages
 */
export function buildCategoryBreadcrumbSchema(categoryName: string, categorySlug: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: HOME_CANONICAL_URL,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: categoryName,
        item: toAbsoluteUrl(`/category/${categorySlug}`),
      },
    ],
  };
}

/**
 * FAQPage Schema for Games with FAQs
 */
export interface FAQItem {
  question: string;
  answer: string;
}

export function buildFAQSchema(faqs: FAQItem[]) {
  if (!faqs || faqs.length === 0) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

/**
 * Combine multiple schemas into a single @graph, stripping redundant
 * per-schema @context declarations per JSON-LD best practice.
 */
export function combineSchemas(...schemas: any[]) {
  const cleanedSchemas = schemas
    .filter(Boolean)
    .map(({ '@context': _ctx, ...rest }) => rest);
  return {
    '@context': 'https://schema.org',
    '@graph': cleanedSchemas,
  };
}
